import React, { useState, useMemo } from 'react';
import { useBookmarkStore } from '../store/bookmarkStore';
import styled, { css, DefaultTheme } from 'styled-components';
import { Folder, Globe, ChevronRight, Search, Star, Tag, Calendar, ChevronLeft, ArrowLeft, ArrowRight } from 'lucide-react';
import { ContextMenu } from './ContextMenu';
import { Bookmark } from '../types/bookmark';

// 修改ThemedProps接口，使其与DefaultTheme兼容
interface ThemedProps {
  theme?: any; // 使用any类型暂时解决类型错误
}

// 添加一个辅助函数来检查主题是否为暗色
const isDarkTheme = (theme?: any): boolean => theme === 'dark';

// 添加Tooltip组件
const Tooltip = styled.div<ThemedProps>`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  background: ${props => isDarkTheme(props.theme) ? '#333' : '#fff'};
  border: 1px solid ${props => isDarkTheme(props.theme) ? '#444' : '#eaeaea'};
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  color: ${props => isDarkTheme(props.theme) ? '#e0e0e0' : '#333'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  max-width: 300px;
  width: max-content;
  white-space: normal;
  line-height: 1.5;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease;
  pointer-events: none;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
  transform: translateY(-5px);

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => isDarkTheme(props.theme) ? '#444' : '#f1f1f1'};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => isDarkTheme(props.theme) ? '#666' : '#ccc'};
    border-radius: 2px;
  }
`;

const GalleryContainer = styled.div<ThemedProps>`
  display: flex;
  height: calc(100vh - 60px);
  background: ${props => props.theme === 'dark' ? '#1a1a1a' : '#f8f9fa'};
  color: ${props => props.theme === 'dark' ? '#e0e0e0' : 'inherit'};
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CategoryTree = styled.div<ThemedProps & { $isCollapsed?: boolean }>`
  width: ${props => props.$isCollapsed ? '60px' : '260px'};
  background: ${props => props.theme === 'dark' ? '#242424' : '#ffffff'};
  border-right: 1px solid ${props => props.theme === 'dark' ? '#333' : '#eaeaea'};
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: ${props => props.theme === 'dark' ? 'none' : '2px 0 4px rgba(0, 0, 0, 0.02)'};
  transition: width 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    max-height: ${props => props.$isCollapsed ? '60px' : '300px'};
    border-right: none;
    border-bottom: 1px solid ${props => props.theme === 'dark' ? '#333' : '#eaeaea'};
    padding: 12px;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme === 'dark' ? '#1a1a1a' : '#f1f1f1'};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme === 'dark' ? '#444' : '#ddd'};
    border-radius: 2px;
  }
`;

const ToggleCategoryButton = styled.button<ThemedProps>`
  position: absolute;
  top: 16px;
  right: 16px;
  background: ${props => props.theme === 'dark' ? '#333' : '#f0f0f0'};
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  
  @media (max-width: 768px) {
    top: 12px;
    right: 12px;
  }
  
  &:hover {
    background: ${props => props.theme === 'dark' ? '#444' : '#e0e0e0'};
  }
`;

const SearchInput = styled.div<ThemedProps>`
  position: relative;

  input {
    width: 100%;
    padding: 10px 12px 10px 36px;
    border: 1px solid ${props => props.theme === 'dark' ? '#333' : '#eaeaea'};
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: all 0.2s ease;
    background: ${props => props.theme === 'dark' ? '#333' : '#f8f9fa'};
    color: ${props => props.theme === 'dark' ? '#e0e0e0' : 'inherit'};

    &:focus {
      border-color: #1a73e8;
      box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.1);
      background: ${props => props.theme === 'dark' ? '#3a3a3a' : '#ffffff'};
    }

    &::placeholder {
      color: ${props => props.theme === 'dark' ? '#666' : '#999'};
    }
  }

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 14px;
    height: 14px;
    color: ${props => props.theme === 'dark' ? '#666' : '#999'};
  }
`;

const MainContent = styled.div<ThemedProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 0;
  background: ${props => props.theme === 'dark' ? '#1a1a1a' : '#f8f9fa'};
`;

const ContentHeader = styled.div`
  padding: 24px 32px;
  background: #fff;
  border-bottom: 1px solid #eaeaea;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);

  @media (max-width: 1400px) {
    padding: 20px 24px;
  }

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

const CategoryTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    width: 20px;
    height: 20px;
    color: #666;
  }
`;

const CategoryStats = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 20px;
  color: #666;
  font-size: 13px;

  span {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background: #f8f9fa;
    border-radius: 6px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const BookmarkGrid = styled.div`
  flex: 1;
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(4, 1fr); /* 默认每行显示4个卡片 */
  gap: 16px;
  max-width: 1600px; /* 增加最大宽度以适应4个卡片 */
  margin: 0 auto;
  width: 100%;
  align-items: start;
  overflow-y: auto;
  
  @media (max-width: 1600px) {
    grid-template-columns: repeat(3, 1fr); /* 大屏幕显示3个卡片 */
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr); /* 中等屏幕显示2个卡片 */
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(1, 1fr); /* 小屏幕显示1个卡片 */
    padding: 12px;
    gap: 12px;
  }
`;

interface BookmarkCardProps {
  isFavorite?: boolean;
}

const BookmarkCard = styled.div<BookmarkCardProps>`
  display: flex;
  flex-direction: column;
  background: ${props => isDarkTheme(props.theme) ? '#242424' : '#ffffff'};
  border: 1px solid ${props => isDarkTheme(props.theme) ? '#333' : '#eaeaea'};
  border-radius: 12px;
  padding: 16px;
  color: inherit;
  transition: all 0.2s ease;
  position: relative;
  width: 100%;
  height: 200px; /* 固定高度 */
  min-height: 200px; /* 确保最小高度 */
  max-height: 200px; /* 确保最大高度 */
  box-sizing: border-box;
  box-shadow: ${props => isDarkTheme(props.theme) ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.02)'};
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr auto; /* 标题、内容、底部元数据的布局 */

  @media (max-width: 768px) {
    padding: 12px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => isDarkTheme(props.theme) 
      ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
      : '0 4px 12px rgba(0, 0, 0, 0.08)'};
    border-color: ${props => isDarkTheme(props.theme) ? '#444' : '#e0e0e0'};
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #1a73e8, #34a853);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover:before {
    opacity: 1;
  }

  ${props => props.isFavorite && css`
    &::after {
      content: '';
      position: absolute;
      top: -1px;
      right: 16px;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid #ffd700;
    }
  `}
`;

const BookmarkLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: block;
  margin: 32px 0 8px 0; /* 增加上边距，为CategoryTag留出空间 */
  width: 100%;

  &:hover h3 {
    color: #1a73e8;
  }
`;

const BookmarkTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  color: ${props => isDarkTheme(props.theme) ? '#e0e0e0' : '#333'};
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-weight: 500;
  line-height: 1.4;
  max-height: 42px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
  transition: color 0.2s ease;
  position: relative;

  @media (max-width: 768px) {
    font-size: 14px;
    -webkit-line-clamp: 1; /* 移动端只显示一行标题 */
    max-height: 20px;
  }

  svg {
    width: 14px;
    height: 14px;
    color: ${props => isDarkTheme(props.theme) ? '#888' : '#666'};
    flex-shrink: 0;
    margin-top: 3px; /* 图标垂直对齐调整 */
  }
`;

const TitleContainer = styled.div`
  position: relative;
  
  &:hover ${Tooltip} {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const BookmarkDescription = styled.p`
  margin: 0 0 8px 0;
  font-size: 13px;
  color: ${props => isDarkTheme(props.theme) ? '#999' : '#666'};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
  max-height: 36px;
  word-break: break-all;

  @media (max-width: 768px) {
    font-size: 12px;
    -webkit-line-clamp: 1; /* 移动端只显示一行描述 */
    max-height: 17px;
  }
`;

const DescriptionContainer = styled.div`
  position: relative;
  
  &:hover ${Tooltip} {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: auto;
  padding-bottom: 8px;
  max-height: 48px;
  overflow: hidden;

  @media (max-width: 768px) {
    max-height: 28px; /* 移动端减小标签区域高度 */
  }
`;

const TagItem = styled.span`
  font-size: 12px;
  color: #1a73e8;
  background: ${props => isDarkTheme(props.theme) ? 'rgba(26, 115, 232, 0.1)' : '#f0f7ff'};
  padding: 3px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 2px 6px;
  }

  &:hover {
    background: ${props => isDarkTheme(props.theme) ? 'rgba(26, 115, 232, 0.2)' : '#e4efff'};
  }
`;

interface CategoryItemProps {
  depth: number;
  isSelected: boolean;
  hasChildren?: boolean;
}

const CategoryItem = styled.div<CategoryItemProps>`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => props.isSelected ? '#1a73e8' : '#444'};
  background: ${props => props.isSelected ? '#f0f7ff' : 'transparent'};
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  margin: 1px 0;

  &:hover {
    background: #f0f7ff;
    color: #1a73e8;
    
    svg {
      color: #1a73e8;
    }
  }
`;

const CategoryItemContent = styled.div<{ depth: number }>`
  display: flex;
  align-items: center;
  padding-left: ${props => props.depth * 20}px;
  flex: 1;
  min-width: 0; // 防止文本溢出
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 20px;
  height: 20px;
`;

const FolderWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  
  svg {
    width: 16px;
    height: 16px;
    color: #666;
  }
`;

const ChevronIcon = styled(ChevronRight)<{ $expanded: boolean }>`
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: rotate(${props => props.$expanded ? '90deg' : '0deg'});
  opacity: 0.7;
`;

const CategoryBadge = styled.span`
  font-size: 12px;
  color: #666;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 4px;
  min-width: 20px;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  background: #ffffff;
  border-radius: 12px;
  margin: 16px auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  max-width: 400px;
  width: calc(100% - 32px);
  
  svg {
    width: 48px;
    height: 48px;
    color: #ccc;
    margin-bottom: 16px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const CategoryLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const BookmarkMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${props => isDarkTheme(props.theme) ? '#777' : '#888'};
  margin-top: 8px;

  @media (max-width: 768px) {
    font-size: 11px;
  }

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const CategoryTag = styled.span`
  font-size: 12px;
  color: ${props => isDarkTheme(props.theme) ? '#999' : '#666'};
  background: ${props => isDarkTheme(props.theme) ? '#333' : '#f0f0f0'};
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  position: absolute;
  top: 12px;
  left: 12px;
  transition: all 0.2s ease;
  z-index: 1; /* 确保分类标签在最上层 */

  svg {
    width: 12px;
    height: 12px;
    color: ${props => isDarkTheme(props.theme) ? '#888' : '#666'};
  }

  &:hover {
    background: ${props => isDarkTheme(props.theme) ? '#444' : '#e6e6e6'};
  }
`;

const BookmarkActions = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${BookmarkCard}:hover & {
    opacity: 1;
  }
`;

interface ActionButtonProps {
  isFavorite?: boolean;
  theme?: any;
}

const ActionButton = styled.button<ActionButtonProps>`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${props => isDarkTheme(props.theme) ? '#666' : '#999'};
  transition: color 0.2s ease;

  &:hover {
    color: ${props => isDarkTheme(props.theme) ? '#e0e0e0' : '#333'};
  }

  &.favorite {
    color: ${props => props.isFavorite ? '#ffd700' : isDarkTheme(props.theme) ? '#666' : '#999'};
    
    &:hover {
      color: #ffd700;
    }
  }
`;

const PaginationContainer = styled.div<ThemedProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 32px;
  background: ${props => isDarkTheme(props.theme) ? '#242424' : '#ffffff'};
  border-top: 1px solid ${props => isDarkTheme(props.theme) ? '#333' : '#eaeaea'};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }
`;

const PaginationInfo = styled.div<ThemedProps>`
  font-size: 14px;
  color: ${props => isDarkTheme(props.theme) ? '#aaa' : '#666'};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled.button<ThemedProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid ${props => isDarkTheme(props.theme) ? '#444' : '#ddd'};
  background: ${props => isDarkTheme(props.theme) ? '#333' : '#fff'};
  color: ${props => isDarkTheme(props.theme) ? '#ccc' : '#666'};
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background: ${props => isDarkTheme(props.theme) ? '#444' : '#f5f5f5'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationNumbers = styled.div`
  display: flex;
  gap: 4px;
`;

interface PageNumberProps extends ThemedProps {
  $isActive: boolean;
}

const PageNumber = styled.button<PageNumberProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid ${props => props.$isActive 
    ? (isDarkTheme(props.theme) ? '#666' : '#0066cc') 
    : (isDarkTheme(props.theme) ? '#444' : '#ddd')};
  background: ${props => props.$isActive 
    ? (isDarkTheme(props.theme) ? '#555' : '#e6f0fa') 
    : (isDarkTheme(props.theme) ? '#333' : '#fff')};
  color: ${props => props.$isActive 
    ? (isDarkTheme(props.theme) ? '#fff' : '#0066cc') 
    : (isDarkTheme(props.theme) ? '#ccc' : '#666')};
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background: ${props => isDarkTheme(props.theme) ? '#444' : '#f5f5f5'};
  }
`;

const PageSizeSelector = styled.select<ThemedProps>`
  height: 32px;
  border-radius: 4px;
  border: 1px solid ${props => isDarkTheme(props.theme) ? '#444' : '#ddd'};
  background: ${props => isDarkTheme(props.theme) ? '#333' : '#fff'};
  color: ${props => isDarkTheme(props.theme) ? '#ccc' : '#666'};
  padding: 0 8px;
  margin-left: 8px;
  
  &:focus {
    outline: none;
    border-color: ${props => isDarkTheme(props.theme) ? '#666' : '#0066cc'};
  }
`;

export const BookmarkGallery: React.FC = () => {
  const { bookmarks, categories, theme, toggleBookmarkFavorite } = useBookmarkStore();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; url: string } | null>(null);
  const [isCategoryCollapsed, setIsCategoryCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12); // 每页显示12个书签

  // 获取分类的直接子分类
  const getChildCategories = (parentId: string | null) => {
    return categories
      .filter(cat => {
        // 如果是顶级分类，parentId 应该是 null 或 undefined
        if (parentId === null) {
          return !cat.parentId;
        }
        // 否则匹配指定的 parentId
        return cat.parentId === parentId;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  // 获取分类下的所有子分类ID（包括自身）
  const getAllChildrenIds = (categoryId: string): string[] => {
    const result = new Set<string>();
    
    const addChildren = (id: string) => {
      result.add(id);
      categories
        .filter(cat => cat.parentId === id)
        .forEach(child => addChildren(child.id));
    };
    
    addChildren(categoryId);
    return Array.from(result);
  };

  // 获取分类的所有父分类ID（从根到当前分类）
  const getParentIds = (categoryId: string | null): string[] => {
    const result: string[] = [];
    let current = categoryId;
    
    while (current) {
      const category = categories.find(cat => cat.id === current);
      if (!category?.parentId) break;
      result.unshift(category.parentId); // 添加到开头，保持从根到叶的顺序
      current = category.parentId;
    }
    
    return result;
  };

  // 获取分类下的书签数量（包括子分类）
  const getCategoryBookmarkCount = (categoryId: string | null): number => {
    if (categoryId === null) {
      return bookmarks.length;
    }
    const childIds = getAllChildrenIds(categoryId);
    return bookmarks.filter(bookmark => 
      childIds.includes(bookmark.categoryId || '')
    ).length;
  };

  // 检查分类是否有子分类
  const hasChildren = (categoryId: string) => {
    return categories.some(cat => cat.parentId === categoryId);
  };

  // 切换分类展开状态
  const toggleCategory = (categoryId: string, forceExpand?: boolean) => {
    const newExpanded = new Set(expandedCategories);
    const shouldExpand = forceExpand ?? !newExpanded.has(categoryId);

    if (!shouldExpand) {
      // 折叠：移除自身和所有子分类
      const childIds = getAllChildrenIds(categoryId);
      childIds.forEach(id => newExpanded.delete(id));
    } else {
      // 展开：添加自身和所有父分类
      newExpanded.add(categoryId);
      const parentIds = getParentIds(categoryId);
      parentIds.forEach(id => newExpanded.add(id));
    }

    setExpandedCategories(newExpanded);
  };

  // 自动展开到指定分类
  const expandToCategory = (categoryId: string) => {
    const parentIds = getParentIds(categoryId);
    const newExpanded = new Set(expandedCategories);
    [...parentIds, categoryId].forEach(id => newExpanded.add(id));
    setExpandedCategories(newExpanded);
  };

  // 处理分类选择
  const handleCategorySelect = (categoryId: string | null) => {
    setSelectedCategory(categoryId);
    if (categoryId) {
      expandToCategory(categoryId);
    }
  };

  // 渲染分类树
  const renderCategoryTree = (parentId: string | null, depth = 0) => {
    const categoryItems = getChildCategories(parentId);
    if (categoryItems.length === 0) return null;
    
    return categoryItems.map(category => {
      const hasChildCategories = hasChildren(category.id);
      const isExpanded = expandedCategories.has(category.id);
      const bookmarkCount = getCategoryBookmarkCount(category.id);

      // 如果有搜索查询，只显示匹配的分类及其父分类
      if (searchQuery) {
        const matchesSearch = category.name.toLowerCase().includes(searchQuery.toLowerCase());
        const hasMatchingChildren = hasChildCategories && 
          categories.some(child => 
            child.parentId === category.id &&
            child.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
        const isParentOfMatching = categories.some(child => {
          const childParents = getParentIds(child.id);
          return childParents.includes(category.id) &&
            child.name.toLowerCase().includes(searchQuery.toLowerCase());
        });

        if (!matchesSearch && !hasMatchingChildren && !isParentOfMatching) {
          return null;
        }
      }

      return (
        <React.Fragment key={category.id}>
          <CategoryItem 
            depth={depth}
            isSelected={selectedCategory === category.id}
            hasChildren={hasChildCategories}
            onClick={(e) => {
              e.stopPropagation();
              handleCategorySelect(category.id);
            }}
          >
            <CategoryItemContent depth={depth}>
              <IconWrapper>
                {hasChildCategories && (
                  <ChevronIcon 
                    $expanded={isExpanded}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory(category.id);
                    }}
                  />
                )}
              </IconWrapper>
              <FolderWrapper>
                <Folder />
              </FolderWrapper>
              <CategoryLabel>{category.name}</CategoryLabel>
            </CategoryItemContent>
            {bookmarkCount > 0 && (
              <CategoryBadge>{bookmarkCount}</CategoryBadge>
            )}
          </CategoryItem>
          {isExpanded && renderCategoryTree(category.id, depth + 1)}
        </React.Fragment>
      );
    });
  };

  const filteredBookmarks = useMemo(() => {
    let filtered = selectedCategory
      ? bookmarks.filter(bookmark => {
          const categoryIds = getAllChildrenIds(selectedCategory);
          return categoryIds.includes(bookmark.categoryId || '');
        })
      : bookmarks;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(bookmark => 
        bookmark.title.toLowerCase().includes(query) ||
        bookmark.url.toLowerCase().includes(query) ||
        bookmark.description?.toLowerCase().includes(query) ||
        bookmark.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // 按更新时间降序排序
    return filtered.sort((a, b) => b.updatedAt - a.updatedAt);
  }, [bookmarks, selectedCategory, searchQuery]);

  // 计算总页数
  const totalPages = Math.ceil(filteredBookmarks.length / itemsPerPage);
  
  // 获取当前页的书签
  const currentPageBookmarks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredBookmarks.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBookmarks, currentPage, itemsPerPage]);

  // 页面变化处理函数
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 当筛选条件变化时，重置为第一页
  useMemo(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  const currentCategory = selectedCategory 
    ? categories.find(cat => cat.id === selectedCategory)
    : null;

  const stats = useMemo(() => {
    const favoriteCount = filteredBookmarks.filter(b => b.isFavorite).length;
    const withTagsCount = filteredBookmarks.filter(b => b.tags.length > 0).length;
    return { total: filteredBookmarks.length, favoriteCount, withTagsCount };
  }, [filteredBookmarks]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // 获取分类名称
  const getCategoryName = (categoryId?: string): string => {
    if (!categoryId) return '未分类';
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || '未分类';
  };

  const renderBookmarkCard = (bookmark: Bookmark) => {
    return (
      <BookmarkCard 
        key={bookmark.id} 
        isFavorite={bookmark.isFavorite}
        onContextMenu={(e) => {
          e.preventDefault();
          setContextMenu({
            x: e.clientX,
            y: e.clientY,
            url: bookmark.url
          });
        }}
      >
        {/* 顶部区域：标题和标签 */}
        <div>
          <CategoryTag>
            <Folder />
            {getCategoryName(bookmark.categoryId)}
          </CategoryTag>
          <BookmarkActions>
            <ActionButton
              className="favorite"
              onClick={() => toggleBookmarkFavorite(bookmark.id)}
              title={bookmark.isFavorite ? "取消收藏" : "收藏"}
              isFavorite={bookmark.isFavorite}
            >
              <Star fill={bookmark.isFavorite ? "#ffd700" : "none"} />
            </ActionButton>
          </BookmarkActions>
          <BookmarkLink href={bookmark.url} target="_blank" rel="noopener noreferrer">
            <TitleContainer>
              <BookmarkTitle>
                {bookmark.favicon ? (
                  <img 
                    src={bookmark.favicon} 
                    alt="" 
                    style={{ width: '14px', height: '14px' }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        const globeIcon = document.createElement('span');
                        globeIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>';
                        parent.insertBefore(globeIcon, e.currentTarget);
                      }
                    }}
                  />
                ) : (
                  <Globe size={14} />
                )}
                {bookmark.title}
              </BookmarkTitle>
              {bookmark.title && bookmark.title.length > 50 && (
                <Tooltip theme={theme}>
                  {bookmark.title}
                </Tooltip>
              )}
            </TitleContainer>
          </BookmarkLink>
        </div>
        
        {/* 中间区域：描述 */}
        <div>
          {bookmark.description ? (
            <DescriptionContainer>
              <BookmarkDescription>
                {bookmark.description}
              </BookmarkDescription>
              {bookmark.description && bookmark.description.length > 70 && (
                <Tooltip theme={theme}>
                  {bookmark.description}
                </Tooltip>
              )}
            </DescriptionContainer>
          ) : (
            <BookmarkDescription style={{ opacity: 0.5 }}>
              暂无描述
            </BookmarkDescription>
          )}
        </div>
        
        {/* 底部区域：标签和元数据 */}
        <div>
          {bookmark.tags.length > 0 ? (
            <TagList>
              {bookmark.tags.slice(0, 3).map((tag: string) => (
                <TagItem key={tag}>{tag}</TagItem>
              ))}
              {bookmark.tags.length > 3 && (
                <TagItem>+{bookmark.tags.length - 3}</TagItem>
              )}
            </TagList>
          ) : (
            <TagList style={{ height: '28px' }}></TagList> // 空标签区域占位
          )}
          
          <BookmarkMeta>
            <span><Calendar />{formatDate(bookmark.createdAt)}</span>
            {bookmark.isFavorite && <span><Star style={{ color: '#ffd700' }} />收藏</span>}
          </BookmarkMeta>
        </div>
      </BookmarkCard>
    );
  };

  const toggleCategoryCollapse = () => {
    setIsCategoryCollapsed(!isCategoryCollapsed);
  };

  return (
    <GalleryContainer theme={theme}>
      <CategoryTree theme={theme} $isCollapsed={isCategoryCollapsed}>
        <ToggleCategoryButton 
          theme={theme}
          onClick={toggleCategoryCollapse}
          title={isCategoryCollapsed ? "展开分类" : "折叠分类"}
        >
          {isCategoryCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </ToggleCategoryButton>
        <SearchInput theme={theme}>
          <Search />
          <input 
            type="text"
            placeholder="搜索书签..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchInput>
        <CategoryItem 
          depth={0} 
          isSelected={selectedCategory === null} 
          onClick={() => handleCategorySelect(null)}
        >
          <CategoryItemContent depth={0}>
            <IconWrapper>
              <div style={{ width: 16 }} />
            </IconWrapper>
            <FolderWrapper>
              <Folder />
            </FolderWrapper>
            <CategoryLabel>全部书签</CategoryLabel>
          </CategoryItemContent>
          <CategoryBadge>{bookmarks.length}</CategoryBadge>
        </CategoryItem>
        {renderCategoryTree(null)}
      </CategoryTree>
      <MainContent theme={theme}>
        <ContentHeader>
          <CategoryTitle>
            <Folder />
            {currentCategory?.name || '全部书签'}
            {currentCategory?.description && (
              <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                {currentCategory.description}
              </span>
            )}
          </CategoryTitle>
          <CategoryStats>
            <span><Globe />{stats.total} 个书签</span>
            <span><Star />{stats.favoriteCount} 个收藏</span>
            <span><Tag />{stats.withTagsCount} 个标签</span>
          </CategoryStats>
        </ContentHeader>
        <BookmarkGrid>
          {filteredBookmarks.length > 0 ? (
            currentPageBookmarks.map(bookmark => renderBookmarkCard(bookmark))
          ) : (
            <EmptyState>
              <Folder />
              <p>当前分类下暂无书签</p>
            </EmptyState>
          )}
        </BookmarkGrid>
        
        {/* 分页控件 */}
        {filteredBookmarks.length > 0 && totalPages > 1 && (
          <PaginationContainer theme={theme}>
            <PaginationInfo theme={theme}>
              共 {filteredBookmarks.length} 个书签，第 {currentPage}/{totalPages} 页，当前页显示 {currentPageBookmarks.length} 个书签
            </PaginationInfo>
            <PaginationControls>
              <PaginationButton 
                theme={theme}
                onClick={() => handlePageChange(1)} 
                disabled={currentPage === 1}
                title="首页"
              >
                <ArrowLeft />
                <ArrowLeft style={{ marginLeft: '-10px' }} />
              </PaginationButton>
              <PaginationButton 
                theme={theme}
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                title="上一页"
              >
                <ArrowLeft />
              </PaginationButton>
              
              <PaginationNumbers>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // 显示当前页附近的页码
                  let pageNum = currentPage;
                  if (totalPages <= 5) {
                    // 如果总页数小于等于5，直接显示所有页码
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    // 如果当前页靠近开始，显示前5页
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    // 如果当前页靠近结束，显示后5页
                    pageNum = totalPages - 4 + i;
                  } else {
                    // 否则显示当前页及其前后各2页
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <PageNumber 
                      key={pageNum}
                      theme={theme}
                      $isActive={currentPage === pageNum}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </PageNumber>
                  );
                })}
              </PaginationNumbers>
              
              <PaginationButton 
                theme={theme}
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                title="下一页"
              >
                <ArrowRight />
              </PaginationButton>
              <PaginationButton 
                theme={theme}
                onClick={() => handlePageChange(totalPages)} 
                disabled={currentPage === totalPages}
                title="末页"
              >
                <ArrowRight />
                <ArrowRight style={{ marginLeft: '-10px' }} />
              </PaginationButton>
              
              <PageSizeSelector
                theme={theme}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1); // 重置到第一页
                }}
              >
                <option value="12">12条/页</option>
                <option value="24">24条/页</option>
                <option value="36">36条/页</option>
                <option value="48">48条/页</option>
              </PageSizeSelector>
            </PaginationControls>
          </PaginationContainer>
        )}
      </MainContent>
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          url={contextMenu.url}
          onClose={() => setContextMenu(null)}
          theme={String(theme) === 'dark' ? 'dark' : 'light'}
        />
      )}
    </GalleryContainer>
  );
}; 